import { z } from 'zod';
import { CustomCssSchema } from './custom-css.schema';
import { ElementTreeSchema } from './element.schema';
import { ThemePageSettingsSchema } from './theme-page-settings.schema';
import { SharedLibrarySchema } from './theme-shared.schema';
import { StoreThemeStylesSchema, ThemeStylesSchema } from './theme-styles.schema';
import { ThemeReleaseNotesSchema, ThemeVersionSchema } from './theme-version.schema';
import { StoreLanguageEnum } from './tenant.schema';

// A theme is data: slug, name, kind, direction, styles and an ordered list of pages, each page one
// tree of elements. A theme ships the starting point; what a merchant edits is their own copy of it.

export const ThemePageTypeEnum = z.enum(['home', 'collection', 'product', 'product-cod', 'checkout', 'upsell', 'downsell', 'thankyou', 'article', 'squeeze', 'search', 'cart', 'contact', 'collections-list', 'account', 'not-found']);
export const ThemeKindEnum = z.enum(['store', 'funnel', 'landing']);
export const ThemeDirectionEnum = z.enum(['ltr', 'rtl']);

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * The page set a theme of each kind must ship; a theme missing one of these is a failed theme, not a smaller one.
 * `product-cod` is mandatory for a store because this market sells on the product page with the order form
 * embedded in it — the separate checkout page carries card and wallet providers only.
 */
export const REQUIRED_PAGES: Record<z.infer<typeof ThemeKindEnum>, readonly z.infer<typeof ThemePageTypeEnum>[]> = {
  store: ['home', 'collection', 'product', 'product-cod', 'checkout', 'upsell', 'downsell', 'thankyou'],
  funnel: ['product', 'checkout', 'upsell', 'downsell', 'thankyou'],
  landing: ['article', 'checkout', 'upsell', 'thankyou']
};

/**
 * The nearest page a theme may draw when it does not ship the one that was asked for. The server
 * resolves a storefront read against this table and the browser resolves a mount against the same
 * one, so a shopper and the page they were served can never disagree on which page actually ran.
 */
export const PAGE_NEIGHBOURS: Partial<Record<z.infer<typeof ThemePageTypeEnum>, readonly z.infer<typeof ThemePageTypeEnum>[]>> = {
  'product-cod': ['product', 'home', 'article'],
  product: ['product-cod', 'home', 'article'],
  home: ['article', 'product'],
  collection: ['home', 'product'],
  article: ['home', 'product'],
  squeeze: ['article', 'home'],
  downsell: ['upsell'],
  thankyou: ['home', 'article']
};

// Picks the page a theme actually ships, walking to its nearest sibling and finally to its first page.
export function resolveShippedPage(
  ships: readonly z.infer<typeof ThemePageTypeEnum>[],
  requested: z.infer<typeof ThemePageTypeEnum>
): z.infer<typeof ThemePageTypeEnum> | null {
  const first = ships[0];
  const candidates = [requested, ...(PAGE_NEIGHBOURS[requested] ?? []), ...(first ? [first] : [])];
  return candidates.find((type) => ships.includes(type)) ?? null;
}

/** One page of a theme: which page it is, and the tree it draws. There is no second way to draw one. */
export const ThemePageSchema = z.object({
  type: ThemePageTypeEnum,
  tree: ElementTreeSchema
}).strict();

/**
 * Whether a theme is finished enough for a store to install. It lives in the theme's own file because
 * that is the only place that knows: the seeder used to carry a hand-written list of available slugs,
 * so every new theme needed an edit to a script before anyone could install it. Defaulted rather than
 * required, so a theme.json written before this field existed still parses and still ships.
 */
export const ThemeStatusEnum = z.enum(['draft', 'published']);

const themeShape = z.object({
  slug: z.string().min(2).max(50).regex(SLUG_RE, 'A theme slug is lowercase words separated by dashes'),
  name: z.string().trim().min(1).max(120),
  kind: ThemeKindEnum,
  direction: ThemeDirectionEnum,
  status: ThemeStatusEnum.default('published'),
  /** Which version this file is; a store pinned to an older one is offered Update. Absent reads as 1.0.0. */
  version: ThemeVersionSchema.optional(),
  /** What this version changed, for the Update dialog. Kept beside the theme in the catalog, never in it. */
  releaseNotes: ThemeReleaseNotesSchema.optional(),
  /** The languages the theme's own words are written for, as Shopify's theme store lists a theme's supported languages. */
  languages: z.array(StoreLanguageEnum).min(1).max(3).default(['en']),
  /** Another theme whose ready-made sections this one offers in Add section, for a theme built from the same pieces.
   * Read off the file by the block library; the catalog drops it, so no stored theme carries it. */
  blocksFrom: z.string().min(2).max(50).regex(SLUG_RE, 'A theme slug is lowercase words separated by dashes').optional(),
  styles: ThemeStylesSchema,
  pages: z.array(ThemePageSchema).min(1).max(24)
}).strict();

type ThemeShape = z.infer<typeof themeShape>;

// Rejects a theme whose page set is incomplete or duplicated for the kind it claims to be.
function assertPagesComplete(theme: ThemeShape, ctx: z.RefinementCtx): void {
  const present = theme.pages.map((page) => page.type);
  const duplicated = present.filter((type, index) => present.indexOf(type) !== index);
  for (const type of new Set(duplicated)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pages'], message: `A theme may only define the "${type}" page once` });
  }
  for (const type of REQUIRED_PAGES[theme.kind]) {
    if (!present.includes(type)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pages'], message: `A ${theme.kind} theme must define its "${type}" page` });
    }
  }
}

/** A whole theme, as stored in `themes.default_config` and served to the storefront. */
export const ThemeDefinitionSchema = themeShape.superRefine(assertPagesComplete);

/**
 * One page of a store's active theme, as the storefront receives it. A shopper is sent the palette
 * and the one page they asked for, already merged with the merchant's edits — never the whole theme,
 * whose other pages are of no use to the browser and are megabytes of copy.
 */
export const StorefrontThemePageSchema = z.object({
  slug: z.string(),
  name: z.string(),
  kind: ThemeKindEnum,
  direction: ThemeDirectionEnum,
  /** The page that was actually resolved, which is the requested one unless the theme does not ship it. */
  page: ThemePageTypeEnum,
  /** Every page type this theme ships, so a client can list them without holding their trees. */
  pages: z.array(ThemePageTypeEnum),
  styles: ThemeStylesSchema,
  /** The page itself. Empty roots is a page the merchant has not built yet, never a missing answer. */
  tree: ElementTreeSchema,
  /** What this one page carries beyond its content: its title, its share card, its verification tags. */
  settings: ThemePageSettingsSchema.optional(),
  /** The store's own stylesheet, sent only when it has one, and drawn after the theme's own styles. */
  customCss: CustomCssSchema.optional()
}).strict();

/**
 * Everything a store has done to a theme, as `store_themes.config` holds it: the tokens it repainted,
 * the pages it built, what each page says about itself, and the elements it saved to use again.
 *
 * `version` is 3 because 2 carried `sections` and `layout` — a record of settings keyed by band id, and
 * an order those bands ran in. Neither names anything this build has, so there is no shape left for a
 * version-2 config to be read as.
 */
export const StoreThemeConfigSchema = z.object({
  version: z.literal(3),
  /** The design tokens this store repainted; a token it never touched follows its theme when that moves. */
  styles: StoreThemeStylesSchema.default({}),
  /**
   * The pages the merchant has built, one tree per page. A page with no tree here draws the starter tree
   * its theme ships. Written one page at a time, because nine of them are past what an API body may carry.
   */
  trees: z.record(ThemePageTypeEnum, ElementTreeSchema).optional(),
  /**
   * What each page says about itself — its listing, its share card, the verification tags it carries.
   * It rides in this config rather than in a table of its own so one Save writes it, one undo reverses
   * it, and one version restores it with the page it belongs to.
   */
  pageSettings: z.record(ThemePageTypeEnum, ThemePageSettingsSchema).optional(),
  /**
   * The elements the merchant saved to use again: a copy they can drop on any page, or a live link every
   * page holding it stays in step with. Read through `.catch` so a library this build can no longer parse
   * costs a store its saved elements rather than every token it ever repainted.
   */
  shared: SharedLibrarySchema.catch({}).optional()
}).strict();

/** The one shape the merge and the builder work on. */
export type StoreThemeConfigDto = z.infer<typeof StoreThemeConfigSchema>;
export type StoreSharedLibraryDto = NonNullable<StoreThemeConfigDto['shared']>;

// Re-reads a config whose trees no longer parse — an element type since retired, say — without them, so
// one unreadable page costs a store that page's tree rather than every setting it ever changed.
function readConfigDroppingTrees(stored: unknown): StoreThemeConfigDto | null {
  if (typeof stored !== 'object' || stored === null || !('trees' in stored)) return null;
  const rest: Record<string, unknown> = { ...(stored as Record<string, unknown>) };
  delete rest.trees;
  const parsed = StoreThemeConfigSchema.safeParse(rest);
  return parsed.success ? parsed.data : null;
}

// Reads a stored theme config, treating an unreadable column as a store that has edited nothing rather
// than as an outage: a storefront must still be served its theme.
export function readStoreThemeConfig(stored: unknown): StoreThemeConfigDto {
  const parsed = StoreThemeConfigSchema.safeParse(stored ?? {});
  if (parsed.success) return parsed.data;
  return readConfigDroppingTrees(stored) ?? { version: 3, styles: {} };
}

export type ThemePageType = z.infer<typeof ThemePageTypeEnum>;
export type ThemeKind = z.infer<typeof ThemeKindEnum>;
export type ThemeDirection = z.infer<typeof ThemeDirectionEnum>;
export type ThemeStatus = z.infer<typeof ThemeStatusEnum>;
export type ThemePageDto = z.infer<typeof ThemePageSchema>;
export type ThemeDefinitionDto = z.infer<typeof ThemeDefinitionSchema>;
export type StorefrontThemePageDto = z.infer<typeof StorefrontThemePageSchema>;
